import MaterialIcon from '@components/common/MaterialIcon';
import { getSelectedLanguage } from '@config/i18n';
import { DROPDOWN_TRANSITION_PROPS } from '@constants/common';
import {
  ACCOUNT_PAGE_ROUTE,
  LOGOUT_PAGE_ROUTE,
  SELECT_CUSTOMER_ROUTE,
} from '@constants/routes';
import {
  DEFAULT_ROLES,
  ROLE_LABELS,
} from '@conversed/shared/src/central/constants/permissions';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { type TLanguage, useLanguageSwitch } from '@hooks/useLangaugeSwitcher';
import { useMainSelector } from '@hooks/useMainSelector';
import chatAvatar from '@images/chat_avatar.png';
import clsx from 'clsx';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlagEmoji } from '../../train-ai/utils/FlagEmoji';
import { SidebarProfileLinkRow } from './SidebarProfileLinkRow';
import { SidebarProfileMenuRow } from './SidebarProfileMenuRow';

interface SidebarProfileProps {
  expanded: boolean;
}

export const SidebarProfile: React.FC<SidebarProfileProps> = ({ expanded }) => {
  const {
    user: { currentUser },
  } = useMainSelector();
  const { t } = useTranslation();

  const { selectedCompany } = useCompanySelector();
  const { languageSwitchers, switchLanguage } = useLanguageSwitch();
  const selectedLanguageCode = getSelectedLanguage();

  const selectedLanguage =
    languageSwitchers.find(
      (switcher) => switcher.language === selectedLanguageCode,
    ) || languageSwitchers[0];
  const [languageOpen, setLanguageOpen] = useState(false);

  // Full name when available; falls back to the email so we never show an empty
  // name line. When there is no name, the email is the primary line (not repeated).
  const displayName = [currentUser?.firstName, currentUser?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const hasName = displayName.length > 0;

  // Same role source as the Dashboard sidebar: super admins show the super-admin
  // role, everyone else shows their role(s) on the selected company.
  const roleText = useMemo(() => {
    const roles = currentUser?.isSuperAdmin
      ? [DEFAULT_ROLES.SUPER_ADMIN_ROLE]
      : (selectedCompany?.roles ?? []);

    return roles
      .map((role) => t(ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role))
      .join(', ');
  }, [currentUser?.isSuperAdmin, selectedCompany?.roles, t]);

  const toggleLanguage = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setLanguageOpen((isOpen) => !isOpen);
  };

  const handleSelectLanguage = (language: TLanguage) => {
    switchLanguage(language);
    setLanguageOpen(false);
  };

  return (
    <div className='relative text-gray-500'>
      <Menu>
        {({ open }) => (
          <div>
            <MenuButton
              className={clsx(
                'flex items-center w-[calc(100%-1px)] overflow-hidden border-0 bg-transparent cursor-pointer py-3 hover:bg-primary-hover focus:outline-none focus-visible:outline-none',
                expanded ? 'px-4 gap-2' : 'justify-center px-0',
              )}>
              <img
                src={currentUser?.profilePic || chatAvatar}
                className='w-[30px] h-[30px] rounded-full flex-shrink-0'
                alt='user-avatar'
              />
              {expanded && (
                <>
                  <div className='min-w-0 flex-1 text-left'>
                    <div className='font-medium text-gray-600 text-tiny truncate'>
                      {hasName ? displayName : currentUser?.email}
                    </div>
                    {roleText && (
                      <div className='text-timestamp text-gray-400 truncate uppercase font-mono leading-[normal]'>
                        {roleText}
                      </div>
                    )}
                  </div>
                  <MaterialIcon
                    classes={[
                      'text-lg',
                      'text-gray-400',
                      'flex-shrink-0',
                      'leading-none',
                      open ? 'rotate-180 transition' : 'transition',
                    ]}
                    icon='arrow_drop_up'
                  />
                </>
              )}
            </MenuButton>
            {open && (
              <Transition as='div' {...DROPDOWN_TRANSITION_PROPS}>
                {/* Anchored to the bottom of the sidebar, so the menu opens upward */}
                <MenuItems className='absolute bottom-full mb-2 left-2 w-[260px] bg-white border border-gray-200 rounded-xl shadow-around z-[12] focus:outline-none focus-visible:outline-none overflow-hidden'>
                  <div className='px-3 py-3 border-b border-gray-200 bg-slate-50 flex items-center gap-3'>
                    <img
                      src={currentUser?.profilePic ?? chatAvatar}
                      className='w-9 h-9 rounded-full flex-shrink-0'
                      alt='user-avatar'
                    />
                    <div className='min-w-0'>
                      <div className='text-xs font-semibold text-gray-900 truncate'>
                        {hasName ? displayName : currentUser?.email}
                      </div>
                      {hasName && currentUser?.email && (
                        <div className='text-pico text-gray-500 truncate font-normal'>
                          {currentUser.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <SidebarProfileLinkRow
                    to={ACCOUNT_PAGE_ROUTE}
                    icon='settings'
                    label={t('profile.dropdown.actions.account-settings')}
                    showChevron
                    bordered
                  />
                  <SidebarProfileLinkRow
                    to={SELECT_CUSTOMER_ROUTE}
                    icon='switch_account'
                    label={t('profile.dropdown.actions.switch-studio', {
                      studio: selectedCompany?.name,
                    })}
                    showChevron
                    bordered
                  />
                  <MenuItem>
                    <div
                      className='border-b border-gray-100 select-none'
                      onMouseDown={(event) => event.stopPropagation()}>
                      <div
                        className='cursor-pointer hover:bg-primary-hover'
                        onClick={toggleLanguage}>
                        <SidebarProfileMenuRow
                          icon='translate'
                          label={`${t('profile.dropdown.actions.choose-language')} ${t(selectedLanguage.displayText)}`}
                          trailingIcon='arrow_drop_down'
                          trailingIconRotated={languageOpen}
                        />
                      </div>
                      {languageOpen && (
                        <div
                          className='px-3 pb-2 flex flex-col gap-0.5'
                          onClick={(event) => event.stopPropagation()}>
                          {languageSwitchers.map(
                            ({ displayText, language }) => (
                              <span
                                key={language}
                                className='flex items-center text-tiny text-gray-700 cursor-pointer px-2 py-1 rounded hover:bg-primary-hover'
                                onClick={() => handleSelectLanguage(language)}>
                                <FlagEmoji
                                  languageCode={language}
                                  size={12}
                                  className='mr-2'
                                />
                                {t(displayText)}
                              </span>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </MenuItem>
                  <SidebarProfileLinkRow
                    to={LOGOUT_PAGE_ROUTE}
                    icon='logout'
                    label={t('profile.dropdown.actions.log-out')}
                    danger
                  />
                </MenuItems>
              </Transition>
            )}
          </div>
        )}
      </Menu>
    </div>
  );
};

export default SidebarProfile;
